/**
 * BLRRD pixel-layer compositor — Phase 4.A.layers.1.
 *
 * Stack-evaluates a list of pixel layers above a base Float32 RGBA
 * buffer (the kernel output), producing a composited Float32 RGBA in
 * the destination buffer. All math is in scene-linear ACEScg.
 *
 * Algorithm: W3C "Compositing and Blending Level 1" recommendation;
 *   §5 (Simple Alpha Compositing) → §6 (Compositing). See
 *   docs/algorithms/layers.md for the citation URL.
 * Cross-reference: Porter, T. & Duff, T. 1984 SIGGRAPH "Compositing
 * Digital Images" (the original "over" operator definition).
 *
 * Composite pipeline per layer (W3C §6):
 *   1. Compute effective source alpha:
 *        αs = layerAlpha × layerOpacity × maskCoverage
 *   2. Per-channel blend (NON-premultiplied):
 *        Cs' = (1 − αb) × Cs + αb × B(Cb, Cs)
 *      where αb is the *current backdrop* alpha (the accumulator).
 *      Cs and Cb are non-premultiplied colours.
 *   3. Pre-multiply both sides:
 *        cs = Cs' × αs
 *        cb = Cb  × αb
 *   4. Porter-Duff "over" (W3C §5.1):
 *        co = cs + cb × (1 − αs)
 *        αo = αs + αb × (1 − αs)
 *   5. Store. αb advances to αo, Cb advances to co / αo.
 *
 * Step (2) is the blend "modifier" the W3C spec calls B; we keep it as
 * a per-channel function dispatched from blend-modes.js. The
 * "(1 − αb) × Cs" lift in step (2) is the spec's correction for the
 * case where the backdrop is not fully opaque — when αb = 1 this
 * reduces to Cs' = B(Cb, Cs), the textbook case.
 *
 * Identity short-circuit (CLAUDE.md invariant):
 *   When `isIdentityStack(layers)` is true (empty stack OR no visible
 *   pixel layers), `composite()` returns `baseRgba` BY REFERENCE — no
 *   allocation, no copy. This is what keeps the pixel-hash + worker-
 *   parity baselines byte-identical when no pixel layers exist.
 *
 * Boundary contract:
 *   • Inputs (baseRgba, layer.rgba, layer.mask.rgba) are non-premult
 *     with separate alpha channels.
 *   • Premultiplication happens locally inside the inner loop and is
 *     undone before writing back. The output buffer is non-premult.
 *   • Channel order: RGBA, 4 floats per pixel.
 *
 * Provenance: implemented clean-room from W3C spec + Porter-Duff paper;
 * no GIMP / Photoshop / Krita source was consulted.
 */

import { isIdentityStack, isPixelLayer, selectPixelLayers, clamp01 } from './model.js';
import { resolveBlendFn, resolveNonSeparableBlendFn } from './blend-modes.js';

const ALPHA_EPSILON = 1e-7;

/**
 * Composite a stack of pixel layers above the kernel-rendered base.
 *
 * Identity short-circuit returns `baseRgba` by reference. Non-identity
 * stacks copy `baseRgba` into `dst` first, then accumulate layers in
 * bottom-to-top order.
 *
 * @param {Array<object>} layers       — full layer list; non-pixel kinds skipped
 * @param {number} w                   — canvas width
 * @param {number} h                   — canvas height
 * @param {Float32Array} baseRgba      — kernel output, length w*h*4
 * @param {Float32Array} dst           — destination, length w*h*4
 * @returns {Float32Array}             — `baseRgba` (identity) or `dst` (composited)
 */
export function composite(layers, w, h, baseRgba, dst) {
    if (!(baseRgba instanceof Float32Array)) {
        throw new Error('composite: baseRgba must be Float32Array');
    }
    const expectedLen = w * h * 4;
    if (baseRgba.length !== expectedLen) {
        throw new Error(`composite: baseRgba length ${baseRgba.length} != ${expectedLen}`);
    }
    /* Identity fast path — ref-passthrough. Keeps pixel-hash + worker-
     * parity baselines byte-identical when no pixel layers exist. */
    if (isIdentityStack(layers)) return baseRgba;

    if (!(dst instanceof Float32Array)) {
        throw new Error('composite: dst must be Float32Array');
    }
    if (dst.length !== expectedLen) {
        throw new Error(`composite: dst length ${dst.length} != ${expectedLen}`);
    }
    /* Seed accumulator with the base. We do this once instead of
     * compositing the base "as a layer" so the alpha math stays
     * stable for opaque kernel output (the common case). */
    dst.set(baseRgba);

    const pixelLayers = selectPixelLayers(layers);
    for (let li = 0; li < pixelLayers.length; li++) {
        const layer = pixelLayers[li];
        if (!layer.visible) continue;
        compositeLayer(layer, w, h, dst);
    }
    return dst;
}

/**
 * Composite a single layer over the accumulator `dst` in place. The
 * accumulator carries non-premultiplied RGBA. See file-header comment
 * for the W3C formula derivation.
 *
 * @param {object} layer
 * @param {number} W                   — canvas width
 * @param {number} H                   — canvas height
 * @param {Float32Array} dst           — accumulator, in-place mutation
 */
function compositeLayer(layer, W, H, dst) {
    if (!isPixelLayer(layer)) return;
    const opacity = clamp01(Number.isFinite(layer.opacity) ? layer.opacity : 1);
    if (opacity <= 0) return;

    /* Two blend families: separable (per-channel, 16 modes) and
     * non-separable (per-pixel HSL, 4 modes). We dispatch ONCE here
     * so the inner loop calls only the chosen family.
     *
     * resolveNonSeparableBlendFn returns null when the mode isn't HSL
     * — in that case we fall back to the per-channel hot loop with
     * resolveBlendFn (which itself falls back to Normal for unknown
     * ids; see Codex 4.A.layers.1 unknown-mode tolerance test). */
    const nonSepFn = resolveNonSeparableBlendFn(layer.blendMode);
    const blendFn = nonSepFn ? null : resolveBlendFn(layer.blendMode);
    const src = layer.rgba;
    const lw = layer.w | 0, lh = layer.h | 0;
    const offX = layer.offsetX | 0;
    const offY = layer.offsetY | 0;
    const mask = layer.mask;
    const maskData = mask ? mask.rgba : null;
    const mw = mask ? (mask.w | 0) : 0;

    /* Compute the intersection rectangle on the canvas. Pixels outside
     * the layer footprint are unchanged — we never write to dst for
     * them. This keeps off-canvas layers cheap and lets future tile-
     * based caching invalidate only the modified rect. */
    const x0 = Math.max(0, offX);
    const y0 = Math.max(0, offY);
    const x1 = Math.min(W, offX + lw);
    const y1 = Math.min(H, offY + lh);
    if (x1 <= x0 || y1 <= y0) return;

    for (let y = y0; y < y1; y++) {
        const ly = y - offY;
        const dstRow = y * W;
        const srcRow = ly * lw;
        for (let x = x0; x < x1; x++) {
            const lx = x - offX;
            const dpi = (dstRow + x) << 2;          /* dst pixel index */
            const spi = (srcRow + lx) << 2;         /* src pixel index */

            /* Layer source — non-premult RGBA. */
            const Sr = src[spi];
            const Sg = src[spi + 1];
            const Sb = src[spi + 2];
            const Sa = src[spi + 3];

            /* Optional mask coverage (single channel, [0, 1]). */
            const maskCov = maskData ? clamp01(maskData[srcRow + lx]) : 1;
            /* Effective source alpha (W3C §6 step 1). */
            const aS = clamp01(Sa) * opacity * maskCov;
            if (aS <= ALPHA_EPSILON) continue;

            /* Backdrop (current accumulator state) — non-premult RGBA. */
            const Br = dst[dpi];
            const Bg = dst[dpi + 1];
            const Bb = dst[dpi + 2];
            const Ba = clamp01(dst[dpi + 3]);

            /* Step 2 — blend modifier (W3C §6 step 2).
             * Cs' = (1 − Ba) × Cs + Ba × B(Cb, Cs)
             *
             * When Ba == 1 (opaque backdrop, the common case for the
             * kernel-rendered base) this reduces to the textbook
             * Cs' = B(Cb, Cs). The general form correctly handles
             * mid-stack layers above semi-transparent backdrops.
             *
             * Two blend families:
             *   • Separable (16 modes — Normal/Multiply/Screen/…) call
             *     blendFn per-channel.
             *   • Non-separable HSL (4 modes — Hue/Saturation/Color/
             *     Luminosity, W3C §10.3) need the whole RGB triplet to
             *     compute Sat / Lum; nonSepFn returns a fresh [r,g,b].
             *
             * HDR scene-linear note (Codex 4.A.layers.1 STOP-1):
             *   Blend results are NOT clamped here. The W3C spec
             *   defines the formulas for [0,1] but extends them
             *   analytically beyond that range (Multiply of two HDR
             *   highlights >1 grows; Screen approaches a sensible
             *   ceiling). BLRRD's HDR-fused buffers can carry values
             *   well above 1 in the highlights; clamping at this
             *   stage would destroy the float headroom that the
             *   filmic/sigmoid view transform later compresses. The
             *   HSL family's internal ClipColor is intrinsic to the
             *   algorithm (Adobe pseudocode) and is bounded by Lum,
             *   which can exceed 1 — so HDR luma still survives. */
            const oneMinusBa = 1 - Ba;
            let Csr, Csg, Csb;
            if (nonSepFn) {
                const trip = nonSepFn(Br, Bg, Bb, Sr, Sg, Sb);
                Csr = oneMinusBa * Sr + Ba * trip[0];
                Csg = oneMinusBa * Sg + Ba * trip[1];
                Csb = oneMinusBa * Sb + Ba * trip[2];
            } else {
                Csr = oneMinusBa * Sr + Ba * blendFn(Br, Sr);
                Csg = oneMinusBa * Sg + Ba * blendFn(Bg, Sg);
                Csb = oneMinusBa * Sb + Ba * blendFn(Bb, Sb);
            }

            /* Step 3-4 — premultiplied "over". W3C §5.1 / Porter-Duff 1984.
             *   co = cs + cb × (1 − αs)
             *   αo = αs + αb × (1 − αs)
             * with cs/cb in premultiplied colour space. */
            const csPr = Csr * aS;
            const csPg = Csg * aS;
            const csPb = Csb * aS;
            const cbPr = Br * Ba;
            const cbPg = Bg * Ba;
            const cbPb = Bb * Ba;
            const oneMinusAs = 1 - aS;
            const aO = aS + Ba * oneMinusAs;

            const coR = csPr + cbPr * oneMinusAs;
            const coG = csPg + cbPg * oneMinusAs;
            const coB = csPb + cbPb * oneMinusAs;

            /* Un-premultiply to leave the accumulator in non-premult
             * space for the next layer. Guard against αo == 0 (would
             * mean both backdrop and source were fully transparent;
             * the channel value is meaningless in that case). */
            if (aO > ALPHA_EPSILON) {
                const invAo = 1 / aO;
                dst[dpi]     = coR * invAo;
                dst[dpi + 1] = coG * invAo;
                dst[dpi + 2] = coB * invAo;
                dst[dpi + 3] = aO;
            } else {
                dst[dpi]     = 0;
                dst[dpi + 1] = 0;
                dst[dpi + 2] = 0;
                dst[dpi + 3] = 0;
            }
        }
    }
}

/**
 * Allocate a destination buffer suited for `composite`. Helper so the
 * legacy editor + worker don't need to remember the w*h*4 formula.
 *
 * @param {number} w
 * @param {number} h
 * @returns {Float32Array}
 */
export function allocCompositeBuffer(w, h) {
    return new Float32Array((w | 0) * (h | 0) * 4);
}
